
import React from "react";
import { SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton, useSidebar } from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { Home, Users, Euro, Clipboard, Calendar, FileArchive, Package, Lightbulb, ClipboardList, BarChart, UserPlus, Settings, LifeBuoy, Megaphone, ShieldAlert } from "lucide-react";
import { hasAccessByRole, userRoleToSystemRoles } from "@/utils/roleUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useCommunityScope } from "@/contexts/CommunityScopeContext";
import { normalizeCommunitySectionSelection } from "@/data/communitySections";

type NavGroupProps = {
  title: string;
  children: React.ReactNode;
};

const NavGroup: React.FC<NavGroupProps> = ({ title, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <SidebarGroup className="px-4 py-2.5">
      <SidebarGroupLabel className="px-0 text-sidebar-foreground/50">{title}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu className="gap-1">{items}</SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

type NavItemProps = {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  onboardingId?: string;
};

const NavItem: React.FC<NavItemProps> = ({ to, icon: Icon, children, onboardingId }) => {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const isActive =
    location.pathname === to || (to !== "/app" && location.pathname.startsWith(`${to}/`));

  const handleClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className="h-10 justify-start px-3"
      >
        <Link
          to={to}
          onClick={handleClick}
          aria-current={isActive ? 'page' : undefined}
          data-onboarding={onboardingId}
        >
          <Icon className="mr-3 h-5 w-5 shrink-0" />
          <span className="truncate">{children}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

export const SidebarNavigation: React.FC = () => {
  const { currentUser } = useAuth();
  const { activeCommunity, activeMembershipProfile, isGlobalAdmin } = useCommunityScope();
  const effectiveRole = React.useMemo(() => {
    if (isGlobalAdmin) {
      return currentUser?.role ?? null;
    }
    return activeMembershipProfile?.role ?? null;
  }, [activeMembershipProfile?.role, currentUser?.role, isGlobalAdmin]);
  const paymentsAllowedRoles = ['manager', 'president', 'vocal', 'neighbor'];
  const isRenter = effectiveRole === 'renter';
  const isAdmin = effectiveRole === 'admin';
  const canAccessPaymentsPage = effectiveRole ? paymentsAllowedRoles.includes(effectiveRole) : false;
  const hasAdminAccess = effectiveRole ? hasAccessByRole(effectiveRole, 'admin') : false;
  const supportAllowedRoles = ['admin', 'admin_general', 'president', 'vocal', 'owner'];
  const userRoles = effectiveRole
    ? Array.from(new Set([effectiveRole, ...(userRoleToSystemRoles[effectiveRole] ?? [])])).filter((role): role is string => Boolean(role))
    : [];
  const canViewSupport = userRoles.some(role => supportAllowedRoles.includes(role));
  const configuredSections = React.useMemo(
    () => normalizeCommunitySectionSelection(activeCommunity?.availableSections),
    [activeCommunity?.availableSections]
  );
  const hasSectionConfig = configuredSections.length > 0;
  const isSectionEnabled = React.useCallback(
    (route: string) => {
      if (route === '/app/support') {
        return true;
      }
      if (route.startsWith('/app/admin/') || route === '/app/settings') {
        return hasAdminAccess;
      }
      if (route === '/app/payments' && !canAccessPaymentsPage) {
        return false;
      }
      if (isAdmin) {
        return true;
      }
      if (!hasSectionConfig) {
        return true;
      }
      return configuredSections.includes(route);
    },
    [canAccessPaymentsPage, configuredSections, hasAdminAccess, hasSectionConfig, isAdmin]
  );

  if (isRenter) {
    return (
      <>
        <NavGroup title="General">
          {isSectionEnabled('/app/calendar') && (
            <NavItem to="/app/calendar" icon={Calendar}>Calendario</NavItem>
          )}
        </NavGroup>
        <NavGroup title="Gestión">
          {isSectionEnabled('/app/reservations') && (
            <NavItem to="/app/reservations" icon={Calendar}>Reservas</NavItem>
          )}
        </NavGroup>
        <NavGroup title="Comunicación">
          {isSectionEnabled('/app/announcements') && (
            <NavItem to="/app/announcements" icon={ClipboardList}>Tablón De Anuncios</NavItem>
          )}
        </NavGroup>
      </>
    );
  }

  return (
    <>
      <NavGroup title="General">
        {isSectionEnabled('/app') && (
          <NavItem to="/app" icon={Home} onboardingId="nav-dashboard">Dashboard</NavItem>
        )}
        {!isAdmin && isSectionEnabled('/app/communities') && (
          <NavItem to="/app/communities" icon={Users}>Comunidad</NavItem>
        )}
        {isSectionEnabled('/app/calendar') && (
          <NavItem to="/app/calendar" icon={Calendar}>Calendario</NavItem>
        )}
      </NavGroup>

      <NavGroup title="Gestión">
        {canAccessPaymentsPage && isSectionEnabled('/app/payments') && (
          <NavItem to="/app/payments" icon={Euro}>Pagos y Cuotas</NavItem>
        )}
        {isSectionEnabled('/app/incidents') && (
          <NavItem to="/app/incidents" icon={Clipboard}>Incidencias</NavItem>
        )}
        {isSectionEnabled('/app/meetings') && (
          <NavItem to="/app/meetings" icon={Calendar}>Juntas y Acuerdos</NavItem>
        )}
        {isSectionEnabled('/app/reservations') && (
          <NavItem to="/app/reservations" icon={Calendar}>Reservas</NavItem>
        )}
        {isSectionEnabled('/app/documents') && (
          <NavItem to="/app/documents" icon={FileArchive}>Documentación</NavItem>
        )}
        {isSectionEnabled('/app/providers') && (
          <NavItem to="/app/providers" icon={Package}>Proveedores</NavItem>
        )}
      </NavGroup>

      <NavGroup title="Comunicación">
        {isSectionEnabled('/app/suggestions') && (
          <NavItem to="/app/suggestions" icon={Lightbulb}>Sugerencias y Propuestas</NavItem>
        )}
        {isSectionEnabled('/app/announcements') && (
          <NavItem to="/app/announcements" icon={ClipboardList}>Tablón De Anuncios</NavItem>
        )}
        {isSectionEnabled('/app/quick-polls') && (
          <NavItem to="/app/quick-polls" icon={BarChart}>Encuestas Rápidas</NavItem>
        )}
      </NavGroup>

      {hasAdminAccess && (
        <NavGroup title="Administración">
          {isSectionEnabled('/app/admin/communities') && (
            <NavItem to="/app/admin/communities" icon={Users}>Comunidades</NavItem>
          )}
          {isAdmin && isSectionEnabled('/app/admin/campaigns') && (
            <NavItem to="/app/admin/campaigns" icon={Megaphone}>Campañas</NavItem>
          )}
          {isAdmin && isSectionEnabled('/app/users') && (
            <NavItem to="/app/users" icon={UserPlus}>Usuarios</NavItem>
          )}
          {isAdmin && (
            <NavItem to="/app/admin/audit-logs" icon={ShieldAlert}>Auditoría de Seguridad</NavItem>
          )}
          {isSectionEnabled('/app/settings') && (
            <NavItem to="/app/settings" icon={Settings}>Configuración</NavItem>
          )}
        </NavGroup>
      )}

      {canViewSupport && (
        <NavGroup title="Soporte">
          {isSectionEnabled('/app/support') && (
            <NavItem to="/app/support" icon={LifeBuoy}>Soporte</NavItem>
          )}
        </NavGroup>
      )}
    </>
  );
};
